import React, { useState, useEffect } from 'react';

const Settings = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ saving: false, message: '' });
  const [settings, setSettings] = useState({
    is_enabled: true,
    emergency_mode: false,
    max_positions: 20,
    risk_percentage: 1.0
  });
  const [pairs, setPairs] = useState([
    { symbol: 'EURUSD', enabled: false },
    { symbol: 'GBPUSD', enabled: false },
    { symbol: 'USDJPY', enabled: false },
    { symbol: 'USDCHF', enabled: false },
    { symbol: 'AUDUSD', enabled: false },
    { symbol: 'USDCAD', enabled: false },
    { symbol: 'NZDUSD', enabled: false },
    { symbol: 'EURJPY', enabled: false },
    { symbol: 'GBPJPY', enabled: false },
    { symbol: 'EURGBP', enabled: false }
  ]);

  // Initial data loading
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token');
        if (!token) {
          window.location.href = '/login';
          return;
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch settings
        try {
          const settingsResponse = await fetch('/api/settings', { headers });
          
          if (settingsResponse.status === 401) {
            window.location.href = '/login';
            return;
          }
          
          if (!settingsResponse.ok) {
            throw new Error(`Settings fetch failed: ${settingsResponse.statusText}`);
          }
          
          const settingsData = await settingsResponse.json();
          setSettings(settingsData);
        } catch (err) {
          console.error('Settings fetch error:', err);
          throw new Error('Failed to load settings');
        }

        // Fetch pairs
        try {
          const pairsResponse = await fetch('/api/pairs', { headers });
          
          if (!pairsResponse.ok && pairsResponse.status !== 404) {
            throw new Error(`Pairs fetch failed: ${pairsResponse.statusText}`);
          }
          
          if (pairsResponse.ok) {
            const pairsData = await pairsResponse.json();
            setPairs(pairsData);
          }
        } catch (err) {
          console.warn('Pairs fetch error:', err);
          // Don't throw error for pairs, keep default values
        }

        setLoading(false);
      } catch (err) {
        console.error('Error in fetchData:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Event handlers
  const handleNumberChange = (e, field) => {
    const value = e.target.value;
    setSettings(prev => ({
      ...prev,
      [field]: field === 'risk_percentage' ? parseFloat(value) : parseInt(value)
    }));
  };

  const handlePairToggle = (symbol) => {
    setPairs(currentPairs => 
      currentPairs.map(pair => 
        pair.symbol === symbol 
          ? { ...pair, enabled: !pair.enabled }
          : pair
      )
    );
  };

  const handleSaveSettings = async () => {
    setSaveStatus({ saving: true, message: 'Saving settings...' });
    const token = localStorage.getItem('token');
    
    if (!token) {
      window.location.href = '/login';
      return;
    }

    try {
      // Prepare trading pairs data
      const enabledPairs = pairs.filter(pair => pair.enabled).map(pair => ({
        symbol: pair.symbol,
        enabled: true
      }));

      // Save general settings
      const settingsResponse = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_enabled: settings.is_enabled,
          emergency_mode: settings.emergency_mode,
          max_positions: parseInt(settings.max_positions),
          risk_percentage: parseFloat(settings.risk_percentage)
        })
      });

      if (!settingsResponse.ok) {
        throw new Error('Failed to update general settings');
      }

      // Save trading pairs
      const pairsResponse = await fetch('/api/settings/trading-pairs', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(enabledPairs)
      });

      if (!pairsResponse.ok) {
        throw new Error('Failed to update trading pairs');
      }

      setSaveStatus({ saving: false, message: 'Settings saved successfully!' });
      setTimeout(() => setSaveStatus({ saving: false, message: '' }), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setSaveStatus({ 
        saving: false, 
        message: `Failed to save settings: ${err.message}` 
      });
    }
  };

  // Loading and error states
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-red-600 text-xl mb-4">Error: {error}</div>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>
      
      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Status Message */}
          {saveStatus.message && (
            <div className={`p-4 rounded-md ${
              saveStatus.message.includes('success') 
                ? 'bg-green-100 text-green-700'
                : saveStatus.message.includes('Failed')
                  ? 'bg-red-100 text-red-700'
                  : 'bg-blue-100 text-blue-700'
            }`}>
              {saveStatus.message}
            </div>
          )}

          {/* Risk Settings */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Risk Management</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Max Positions
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  value={settings.max_positions}
                  onChange={(e) => handleNumberChange(e, 'max_positions')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Risk Percentage
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="100"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  value={settings.risk_percentage}
                  onChange={(e) => handleNumberChange(e, 'risk_percentage')}
                />
              </div>
            </div>
          </div>

          {/* Trading Pairs */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Trading Pairs</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {pairs.map((pair) => (
                <div key={pair.symbol} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={pair.symbol}
                    checked={pair.enabled}
                    onChange={() => handlePairToggle(pair.symbol)}
                    className="h-4 w-4 text-blue-600"
                  />
                  <label htmlFor={pair.symbol} className="text-sm">
                    {pair.symbol}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={saveStatus.saving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saveStatus.saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;